import axios, { AxiosError, type AxiosRequestConfig } from 'axios'

import i18n from '@/i18n'

/**
 * The access token lives in an httpOnly cookie set by Django, so the browser
 * attaches it automatically - we only need `withCredentials`. The in-memory
 * copy below is a fallback for cross-origin deployments where the cookie is
 * blocked (e.g. API on a different eTLD+1).
 */
let accessToken: string | null = null

export function setAccessToken(token: string | null) {
  accessToken = token
}

export const api = axios.create({
  baseURL: import.meta.env.VITE_API_URL ?? '/api',
  withCredentials: true,
  headers: { 'Content-Type': 'application/json' },
})

api.interceptors.request.use((config) => {
  // Jazyk aplikácie, nie prehliadača - hlášky servera (`gettext`) a číselníky
  // tak idú v tom istom jazyku ako zvyšok obrazovky.
  if (i18n.language) config.headers['Accept-Language'] = i18n.language
  if (accessToken) {
    config.headers.Authorization = `Bearer ${accessToken}`
  }
  return config
})

// --- silent refresh ---------------------------------------------------------
let refreshing: Promise<string | null> | null = null

async function refreshAccessToken(): Promise<string | null> {
  try {
    const { data } = await axios.post<{ access: string }>(
      `${api.defaults.baseURL}/auth/refresh`,
      {},
      { withCredentials: true },
    )
    setAccessToken(data.access)
    return data.access
  } catch {
    setAccessToken(null)
    return null
  }
}

api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const original = error.config as AxiosRequestConfig & { _retried?: boolean }
    const url = original?.url ?? ''

    // Škola pozastavená pre neuhradenú faktúru - obrazovka s údajmi na úhradu
    // (`SuspendedScreen`) namiesto nezmyselnej chyby prihlásenia.
    const data = error.response?.data as { code?: string; invoice?: unknown } | undefined
    if (error.response?.status === 402 && data?.code === 'tenant.suspendedForInvoice') {
      window.dispatchEvent(new CustomEvent('l2s:suspended', { detail: data.invoice }))
      return Promise.reject(error)
    }

    const isAuthCall = url.includes('/auth/login') || url.includes('/auth/refresh')
    if (error.response?.status !== 401 || original?._retried || isAuthCall) {
      return Promise.reject(error)
    }

    original._retried = true
    refreshing = refreshing ?? refreshAccessToken().finally(() => { refreshing = null })
    const token = await refreshing

    if (!token) {
      // Iná karta mohla tokeny medzitým obnoviť (cookies sú spoločné) - vtedy
      // náš pokus narazil na už zneplatnený token, ale požiadavka prejde.
      try {
        return await api(original)
      } catch (retryError) {
        window.dispatchEvent(new CustomEvent('l2s:unauthenticated'))
        return Promise.reject(retryError)
      }
    }
    return api(original)
  },
)

/** Pull a readable message out of a DRF error response. */
export function apiErrorMessage(error: unknown, fallback = 'Nastala chyba.'): string {
  if (axios.isAxiosError(error)) {
    const data = error.response?.data as
      | { detail?: string; code?: string; [key: string]: unknown }
      | undefined
    if (typeof data?.detail === 'string') return data.detail
    if (data && typeof data === 'object') {
      const first = Object.values(data).flat()[0]
      if (typeof first === 'string') return first
    }
    if (error.message) return error.message
  }
  return fallback
}

/** Machine-readable code the backend attaches to domain errors. */
export function apiErrorCode(error: unknown): string | null {
  if (axios.isAxiosError(error)) {
    const data = error.response?.data as { code?: string } | undefined
    return data?.code ?? null
  }
  return null
}
